import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTab } from '../../contexts/TabContext';
import './TabBar.css';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * TabBar - 탭 바 네비게이션 컴포넌트
 *
 * 멀티탭 인터페이스의 탭 전환, 닫기, 컨텍스트 메뉴(우클릭) 기능을 제공한다.
 */
export function TabBar() {
  const {
    tabs,
    activeTabId,
    showHome,
    activateTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    closeAllTabs,
  } = useTab();
  const [contextMenu, setContextMenu] = useState(null);
  const menuRef = useRef(null);

  // Close context menu on outside click or Escape
  const handleCloseMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => handleCloseMenu();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCloseMenu();
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu, handleCloseMenu]);

  const handleContextMenu = useCallback((e, tab) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      tabId: tab.id,
    });
  }, []);

  const tabIndex = contextMenu ? tabs.findIndex((t) => t.id === contextMenu.tabId) : -1;
  const hasTabsToRight = tabIndex >= 0 && tabIndex < tabs.length - 1;
  const hasOtherTabs = tabs.length > 1;

  if (tabs.length === 0) return null;

  return (
    <div className="tab-bar">
      <div className="tab-bar-list">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`tab-bar-item ${tab.id === activeTabId && !showHome ? 'active' : ''}`}
            onClick={() => activateTab(tab.id)}
            onMouseDown={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                closeTab(tab.id);
              }
            }}
            onContextMenu={(e) => handleContextMenu(e, tab)}
          >
            <span className="tab-bar-label">{tab.label}</span>
            <button
              className="tab-bar-close"
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.id);
              }}
              title="닫기"
            >
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={menuRef}
          className="tab-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="tab-context-menu-item"
            onClick={() => {
              closeTab(contextMenu.tabId);
              handleCloseMenu();
            }}
          >
            탭 닫기
          </button>
          <button
            className="tab-context-menu-item"
            onClick={() => {
              closeOtherTabs(contextMenu.tabId);
              handleCloseMenu();
            }}
            disabled={!hasOtherTabs}
          >
            다른 탭 모두 닫기
          </button>
          <button
            className="tab-context-menu-item"
            onClick={() => {
              closeTabsToRight(contextMenu.tabId);
              handleCloseMenu();
            }}
            disabled={!hasTabsToRight}
          >
            오른쪽 탭 닫기
          </button>
          <div className="tab-context-menu-divider" />
          <button
            className="tab-context-menu-item tab-context-menu-item--danger"
            onClick={() => {
              closeAllTabs();
              handleCloseMenu();
            }}
          >
            모든 탭 닫기
          </button>
        </div>
      )}
    </div>
  );
}
